<script lang="ts" setup>
import type { CombinedError } from '@urql/core';

import { Button } from '~ds/components/ui/button';
import { Label } from '~ds/components/ui/label';
import { RadioGroup, RadioGroupItem } from '~ds/components/ui/radio-group';
import { Separator } from '~ds/components/ui/separator';
import { Heading, Text } from '~ds/components/ui/typography';

import type { PendingAccountChoicesQuery, SelectedEntityInput, SiteRoleType } from '~/graphql/generated/graphql';
import type { SignInContinuation } from '~/types/login';

import { LoginFlowType } from '~/graphql/generated/graphql';
import { providerLoginFailureLocation } from '~/utils/providerLogin';
import { groupSchoolAccountChoices, SCHOOL_ACCOUNT_ROLE_LABEL_KEYS } from '~/utils/schoolAccountChoices';

type AccountChoice = PendingAccountChoicesQuery['core']['auth']['pendingAccountChoices'][number];

interface Props {
  roleType: SiteRoleType;
  /** The arrival entity the sign-in carried, kept the way the code and password steps keep it. */
  selectedEntity?: SelectedEntityInput | null;
}

const props = withDefaults(defineProps<Props>(), {
  selectedEntity: null,
});
const emit = defineEmits<{
  chosen: [result: SignInContinuation];
  restart: [message: string];
}>();
const loading = defineModel<boolean>('loading', { default: false });

const { t } = useI18n();
const { pendingAccountChoices, chooseAccount } = useSchoolsSignIn();

const choices = ref<AccountChoice[]>([]);
const selectedAccountId = ref<string | undefined>();
const errorMessage = ref<string | null>(null);
const loadFailed = ref(false);

const sections = computed(() => groupSchoolAccountChoices(choices.value));

const describeError = (error: CombinedError | string | null) =>
  typeof error === 'string' || !error ? t('general.unexpectedError') : getFirstErrorMessageFromCombinedErrors(t, error);

const startAgain = () => {
  loading.value = false;
  emit('restart', t('schools.auth.signInExpired'));
};

const loadChoices = async () => {
  loading.value = true;
  errorMessage.value = null;
  loadFailed.value = false;

  const { choices: loaded, error } = await pendingAccountChoices();

  if (error) {
    errorMessage.value = describeError(error);
    loadFailed.value = true;
    loading.value = false;
    return;
  }

  // Nothing on offer means the sign-in waiting on this choice is gone, and only a new code brings it back.
  if (loaded.length === 0) {
    startAgain();
    return;
  }

  choices.value = loaded;
  loading.value = false;
};

onMounted(loadChoices);

const submit = async () => {
  const accountId = selectedAccountId.value;
  if (!accountId || loading.value) {
    return;
  }

  loading.value = true;
  errorMessage.value = null;

  const { flow, hasMatchedInvite, passwordChangeToken, providerLoginFailure, error } = await chooseAccount({
    accountId,
    roleType: props.roleType,
    ...(props.selectedEntity ? { selectedEntity: props.selectedEntity } : {}),
  });

  if (!flow) {
    errorMessage.value = describeError(error);
    loading.value = false;
    return;
  }

  // A provider sign-in whose pick was refused ends where that provider's other refusals do.
  if (providerLoginFailure) {
    await navigateTo(providerLoginFailureLocation(providerLoginFailure), { replace: true });
    return;
  }

  if (flow === LoginFlowType.SignInExpired || flow === LoginFlowType.InvalidCredentials) {
    startAgain();
    return;
  }

  emit('chosen', { flow, hasMatchedInvite, passwordChangeToken });
};
</script>

<template>
  <form
    class="flex flex-col"
    data-testid="choose-account-step"
    @submit.prevent="submit"
  >
    <Heading
      size="xl"
      as="h1"
      class="text-center mb-3"
    >
      {{ t('schools.auth.chooseAccount.title') }}
    </Heading>
    <Text size="md" class="text-center text-muted-foreground mb-6">
      {{ t('schools.auth.chooseAccount.description') }}
    </Text>

    <RadioGroup
      v-model="selectedAccountId"
      :aria-label="t('schools.auth.chooseAccount.title')"
      :disabled="loading"
      class="gap-2"
    >
      <template v-for="section in sections" :key="section.key">
        <template v-if="section.isPersonal">
          <Separator class="my-2" />
          <Text
            size="sm"
            class="text-muted-foreground font-medium"
            data-testid="choose-account-personal"
          >
            {{ t('schools.auth.chooseAccount.personal') }}
          </Text>
        </template>
        <Label
          v-for="choice in section.choices"
          :key="choice.accountId"
          :for="`choose-account-${choice.accountId}`"
          class="gap-3 rounded-xl border border-input px-4 py-3 cursor-pointer has-data-[state=checked]:border-primary"
          data-testid="choose-account-row"
        >
          <RadioGroupItem
            :id="`choose-account-${choice.accountId}`"
            :value="choice.accountId"
            :data-testid="`choose-account-option-${choice.accountId}`"
          />
          <span class="flex flex-col gap-1 text-left">
            <span class="text-base">{{ choice.schoolName }}</span>
            <span class="text-muted-foreground font-normal">{{ t(SCHOOL_ACCOUNT_ROLE_LABEL_KEYS[choice.roleType]) }}</span>
          </span>
        </Label>
      </template>
    </RadioGroup>

    <Text
      v-if="errorMessage"
      class="text-destructive text-center mt-4"
      data-testid="choose-account-error"
    >
      {{ errorMessage }}
    </Text>

    <Button
      v-if="loadFailed"
      class="mt-6 w-full"
      variant="outline"
      size="lg"
      type="button"
      data-testid="choose-account-retry"
      @click="loadChoices"
    >
      {{ t('schools.auth.chooseAccount.retry') }}
    </Button>
    <Button
      v-else
      class="mt-6 w-full"
      variant="secondary"
      size="lg"
      type="submit"
      :loading="loading"
      :disabled="!selectedAccountId"
      data-testid="choose-account-submit"
    >
      {{ t('schools.auth.chooseAccount.submit') }}
    </Button>
  </form>
</template>
